import { useState } from "react";
import { toast } from "sonner";
import { Reveal } from "./Reveal";
import { PROFILE } from "../data/work";
import { submitContact } from "../lib/api";

const initial = { name: "", email: "", company: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email and a message.");
      return;
    }
    setLoading(true);
    try {
      await submitContact(form);
      toast.success("Message sent. Srishti will get back to you soon.");
      setForm(initial);
    } catch (err) {
      toast.error("Something went wrong. Please email directly.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-line py-3 text-lg text-ink placeholder:text-muted2/60 focus:outline-none focus:border-vermilion transition-colors duration-300";

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="px-5 md:px-12 pt-24 md:pt-32 pb-16 max-w-[1600px] mx-auto scroll-mt-16"
    >
      <Reveal>
        <p className="text-xs md:text-sm uppercase tracking-[0.25em] text-vermilion mb-5">
          Let’s make something people love, and we too
        </p>
        <h2 className="font-serif text-[13vw] md:text-[8vw] leading-[0.9] tracking-[-0.02em] text-ink">
          Say hello.
        </h2>
      </Reveal>

      <div className="mt-16 md:mt-24 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
        {/* Form */}
        <form
          data-testid="contact-form"
          onSubmit={onSubmit}
          className="lg:col-span-7 order-2 lg:order-1 space-y-8"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-muted2">Name*</label>
              <input
                data-testid="contact-name"
                name="name"
                value={form.name}
                onChange={onChange}
                placeholder="Your name"
                className={inputCls}
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-muted2">Email*</label>
              <input
                data-testid="contact-email"
                name="email"
                type="email"
                value={form.email}
                onChange={onChange}
                placeholder="you@company.com"
                className={inputCls}
              />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-muted2">Company</label>
            <input
              data-testid="contact-company"
              name="company"
              value={form.company}
              onChange={onChange}
              placeholder="Where you’re writing from"
              className={inputCls}
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-muted2">Message*</label>
            <textarea
              data-testid="contact-message"
              name="message"
              value={form.message}
              onChange={onChange}
              rows={4}
              placeholder="Tell me what you’re building."
              className={`${inputCls} resize-none`}
            />
          </div>
          <button
            data-testid="contact-submit"
            type="submit"
            disabled={loading}
            className="group inline-flex items-center gap-3 bg-ink text-paper px-8 py-4 text-sm uppercase tracking-[0.2em] hover:bg-vermilion transition-colors duration-300 disabled:opacity-60"
          >
            {loading ? "Sending…" : "Send Message"}
            <span className="inline-block transition-transform duration-300 group-hover:translate-x-1">→</span>
          </button>
        </form>

        {/* Details */}
        <div className="lg:col-span-5 order-1 lg:order-2 space-y-10">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-muted2 mb-2">Email</p>
            <a
              data-testid="contact-email-link"
              href={`mailto:${PROFILE.email}`}
              className="link-underline font-serif text-2xl md:text-3xl text-ink break-all"
            >
              {PROFILE.email}
            </a>
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-muted2 mb-2">Phone</p>
            <a
              data-testid="contact-phone-link"
              href={`tel:${PROFILE.phone.replace(/\s/g, "")}`}
              className="link-underline font-serif text-2xl md:text-3xl text-ink"
            >
              {PROFILE.phone}
            </a>
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-muted2 mb-2">LinkedIn</p>
            <a
              data-testid="contact-linkedin-link"
              href={PROFILE.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="link-underline font-serif text-2xl md:text-3xl text-ink"
            >
              {PROFILE.linkedinLabel}
            </a>
          </div>
          <a
            data-testid="contact-resume-link"
            href={PROFILE.resumeUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 border border-ink px-6 py-3 text-sm uppercase tracking-[0.2em] text-ink hover:bg-ink hover:text-paper transition-colors duration-300"
          >
            Download Résumé ↗
          </a>
        </div>
      </div>
    </section>
  );
}
